import { Button, Dropdown } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import DevEnrichHint from './DevEnrichHint.jsx';
import { escalateParamsFromEvent } from '../utils/escalateParams.js';
import { eventHasWorkflowTrace } from '../utils/workflowTrace.js';
import { SkipForward, HelpCircle, TrendingUp, Route, MoreVertical } from 'lucide-react';

/**
 * Decision section footer (aims-htl `evm-section--decision` + corner overflow menu).
 * Primary action on the left; Skip / Ask / Escalate / Trace live in the ⋮ menu on the right.
 * `extraMenuItems` (CT-4858) appends Take it / Nudge / Reassign for My Team events,
 * after the always-available ones — the ⋮ order the prototype uses.
 * Per-type decisions (CT-4859+) pass `primaryActions[]`; generic events fall back
 * to the single `event.actionLabel` primary wired to `onPrimary`.
 * `summary` displays a boxed confirm (string or rich node) with its actions.
 * `summaryStacked` stacks content above the buttons (deny + reason textarea).
 */
function DecisionFooter({
  event,
  primaryActions,
  onPrimary,
  onSkip,
  onAsk,
  onEscalate,
  onTrace,
  extraMenuItems = [],
  hideTrace = false,
  hint,
  summary,
  summaryStacked = false,
  sticky = true,
}) {
  const { t } = useTranslation('work-queues-app');
  // Escalate is entity-keyed (CT-5094): only offer it when the event has an
  // escalatable entity (truth / claim / change_request). Hiding it elsewhere
  // avoids surfacing an action that would always fail (e.g. a proposal).
  const canEscalate = Boolean(onEscalate) && escalateParamsFromEvent(event) != null;
  // Trace is gated per EVENT, not per type: the prototype checks
  // `event.sourceWorkflow`, and two rows of the same type can differ (EVT-007
  // gov-review has a trace, EVT-006 does not). `eventHasWorkflowTrace` applies
  // the type ceiling AND checks the data, so today it hides everywhere — no
  // endpoint returns `render.trace` yet (CT-4867 / question P4). Honest state:
  // it used to appear on every type and only fire a toast. `hideTrace` stays as
  // an explicit per-type override (Question opts out regardless).
  const showTrace = !hideTrace && eventHasWorkflowTrace(event);
  // Per-type decisions pass their own primaryActions array (possibly empty, when
  // the decision lives inline in the body). Only when the prop is omitted do we
  // fall back to the single event.actionLabel primary wired to onPrimary.
  const actions =
    primaryActions ??
    (event.actionLabel
      ? [{ id: 'primary', label: event.actionLabel, onClick: () => onPrimary?.(event) }]
      : []);

  const buttons = actions.map((action) => (
    <Button
      key={action.id}
      variant={action.variant ?? 'primary'}
      size={action.size ?? (summary ? 'md' : 'sm')}
      leftIcon={action.icon}
      disabled={action.disabled}
      onClick={action.onClick}
    >
      {action.label}
    </Button>
  ));

  const summaryBody =
    typeof summary === 'string' ? (
      <p className="min-w-0 text-xs leading-relaxed text-content">{summary}</p>
    ) : (
      <div className="min-w-0 w-full">{summary}</div>
    );

  return (
    <div
      className={[
        'shrink-0 border-t border-edge bg-surface px-5 py-4',
        sticky ? 'sticky bottom-0 z-10 bg-surface/95 backdrop-blur' : 'rounded-b-[10px] bg-surface-elevated',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-content-faint">
        {t('decision.label')}
      </p>

      <div className={['flex justify-between gap-4', summary ? 'items-center' : 'items-start'].join(' ')}>
        {summary ? (
          <div
            className={[
              'min-w-0 flex-1 rounded-lg border border-edge bg-surface-elevated px-3.5 py-2.5',
              summaryStacked
                ? 'flex flex-col gap-3'
                : 'flex flex-wrap items-center justify-between gap-3',
            ].join(' ')}
          >
            {summaryBody}
            <div className={['flex flex-wrap items-center gap-2', summaryStacked ? 'justify-end' : ''].join(' ')}>
              {buttons}
            </div>
          </div>
        ) : (
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">{buttons}</div>
            <DevEnrichHint message={hint} />
          </div>
        )}

        <Dropdown
          align="right"
          menuPosition="top"
          usePortal
          trigger={
            <button
              type="button"
              aria-label={t('actions.more')}
              title={t('actions.more')}
              className="inline-flex shrink-0 items-center justify-center rounded-md border border-edge p-1.5 text-content-muted transition-colors hover:bg-surface-hover hover:text-content"
            >
              <MoreVertical size={16} />
            </button>
          }
        >
          <Dropdown.Item icon={SkipForward} onClick={() => onSkip(event.id)}>
            {t('actions.skip')}
          </Dropdown.Item>
          <Dropdown.Item icon={HelpCircle} onClick={() => onAsk(event)}>
            {t('actions.ask')}
          </Dropdown.Item>
          {canEscalate && (
            <Dropdown.Item icon={TrendingUp} onClick={() => onEscalate(event)}>
              {t('actions.escalate')}
            </Dropdown.Item>
          )}
          {showTrace && (
            <Dropdown.Item icon={Route} onClick={() => onTrace(event)}>
              {t('actions.trace')}
            </Dropdown.Item>
          )}
          {/* Team actions come LAST, matching the prototype's ⋮ order
              (Skip · Ask · Escalate · Trace · Take it · Nudge · Reassign) — they
              used to be prepended, so "Take it" was the first thing a manager saw. */}
          {extraMenuItems.length > 0 && <Dropdown.Separator />}
          {extraMenuItems.map((item) => (
            <Dropdown.Item key={item.id} icon={item.icon} onClick={() => item.onClick(event)}>
              {item.label}
            </Dropdown.Item>
          ))}
        </Dropdown>
      </div>
    </div>
  );
}

export default DecisionFooter;
